import { useState, useRef, useEffect } from "react";
import chemistryCalcPool from "./chemistryCalcPool";
import { awardToken } from "../../../awardtoken.js";
import "./ChemistryCalculationPracticeTool.css";

const SHEET_SIZES = [10, 25, 50];

function getRandomProblem(pool) {
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function ChemistryCalculationPracticeTool({ onAwardTokens } = {}) {
  const [userAnswer, setUserAnswer] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const [sheetProblems, setSheetProblems] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false); // only for worksheet mode
  const [selectedTopic, setSelectedTopic] = useState("");
  const tokensAwarded = useRef(0);

  // Prepare topics
  const TOPICS = Array.from(new Set(chemistryCalcPool.map(q => q.type)));
  function getFilteredPool() {
    return selectedTopic ? chemistryCalcPool.filter(q => q.type === selectedTopic) : chemistryCalcPool;
  }

  // Current question state (initialize based on topic)
  const [currentQ, setCurrentQ] = useState(() => getRandomProblem(getFilteredPool()));

  // UseEffect on topic change: no hooks warning
  useEffect(() => {
    const pool = selectedTopic
      ? chemistryCalcPool.filter(q => q.type === selectedTopic)
      : chemistryCalcPool;
    setCurrentQ(getRandomProblem(pool));
    setUserAnswer("");
    setFeedback(null);
    setAttempts(0);
    setCorrect(0);
    setWaitingForNext(false);
  }, [selectedTopic]);

  // Award tokens logic
  useEffect(() => {
    if (correct >= (tokensAwarded.current + 1) * 10) {
      awardToken();
      tokensAwarded.current += 1;
      if (onAwardTokens) onAwardTokens(1);
    }
  }, [correct, onAwardTokens]);

  // Next question handler (depend on selectedTopic)
function nextQuestion() {
  setCurrentQ(getRandomProblem(getFilteredPool()));
  setUserAnswer("");
  setFeedback(null);
  setWaitingForNext(false);
}

function normalizeFormula(input) {
  if (!input) return "";
  // 1. 全部大寫
  let s = input.trim().toUpperCase();
  // 2. 下標轉數字
  const sub = {'₀':'0','₁':'1','₂':'2','₃':'3','₄':'4','₅':'5','₆':'6','₇':'7','₈':'8','₉':'9'};
  s = s.replace(/[\u2080-\u2089]/g, x => sub[x]);
  // 3. L/l統一（可選：也許有 L/i 混淆問題時也做處理）
  s = s.replace(/L/g, "L");
  // 4. 去空格
  s = s.replace(/\s+/g, "");
  return s;
}

function handleAnswerSubmit(e) {
  e.preventDefault();
  setAttempts(a => a + 1);
  const normalizedUser = normalizeFormula(userAnswer);
  const normalizedAns = normalizeFormula(currentQ.answer);
  if (normalizedUser === normalizedAns) {
    setCorrect(c => c + 1);
    setFeedback("✅ Correct!");
    setWaitingForNext(true);
  } else {
    setFeedback(`❌ Incorrect. The answer is: ${currentQ.answer} ${currentQ.units}`);
    setWaitingForNext(true);
  }
}

  // Worksheet mode, generate sheet from filtered pool
  function startSheetMode(count) {
    const pool = getFilteredPool();
    const problems = Array.from({ length: count }, () => getRandomProblem(pool));
    setSheetProblems(problems);
    setShowAnswers(false); // default: do not show answers
    setTimeout(() => window.print(), 100);
  }
  function leaveSheetMode() { setSheetProblems(null); }

  // Worksheet render
  if (sheetProblems) {
    return (
      <div className="worksheet">
        <h2 className="no-print">Chemistry Calculation Worksheet</h2>
        <button className="no-print" onClick={leaveSheetMode}>Back to Practice</button>
        <button
          className="no-print"
          style={{ marginLeft: "1em" }}
          onClick={() => setShowAnswers(v => !v)}
        >
          {showAnswers ? "Hide Answers" : "Show Answers"}
        </button>
        <ol>
          {sheetProblems.map((q, idx) => (
            <li key={idx} style={{ marginBottom: "1.1em" }}>
              <div><b>Q{idx + 1}.</b> {q.question}</div>
              <div>Answer: __________ {q.units}</div>
              {showAnswers && (
                <div style={{ color: "#26b973", fontSize: "0.98em" }}>
                  Correct: {q.answer} {q.units}
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  // Main practice UI
  return (
    <div className="practice-container">
      <h2>Chemistry Calculation Practice</h2>
      <div style={{ margin: "1em 0" }}>
        <label>
          <strong>Practice Topic:</strong>
          <select
            value={selectedTopic}
            onChange={e => setSelectedTopic(e.target.value)}
            style={{ marginLeft: "1em", minWidth: 120 }}
          >
            <option value="">All</option>
            {TOPICS.map(topic =>
              <option key={topic} value={topic}>
                {topic.charAt(0).toUpperCase() + topic.slice(1)}
              </option>
            )}
          </select>
        </label>
      </div>
      <div style={{ margin: "1.3em 0" }}>
        <b>Question:</b> {currentQ.question}
      </div>
      <form onSubmit={handleAnswerSubmit} className="answer-form">
        <input
          type="text"
          value={userAnswer}
          onChange={e => setUserAnswer(e.target.value)}
          autoFocus
          disabled={waitingForNext}
        />
        <span style={{ marginLeft: 10, fontWeight: 500 }}>{currentQ.units}</span>
        <button
          type="submit"
          style={{ marginLeft: "0.7rem" }}
          disabled={waitingForNext}
        >
          Submit
        </button>
      </form>
      {feedback && (
        <div style={{ margin: "1em 0", fontWeight: 500 }}>{feedback}</div>
      )}
      {waitingForNext && (
        <button className="math-pill-btn" style={{ marginTop: "0.7em" }} onClick={nextQuestion}>Next</button>)}<p>
        Score: {correct} / {attempts}</p>
      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map(cnt => (
          <button key={cnt} onClick={() => startSheetMode(cnt)} style={{ marginLeft: "0.5rem" }}>
            {cnt} Questions
          </button>
        ))}
      </div>
    </div>
  );
}
